import * as React from "react"
import { Plus, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

/**
 * The list of email addresses told how a setup's runs went.
 *
 * A feature component beside TargetPicker and MessageEditor rather than a new
 * `components/ui/` primitive: there is no repeatable-text-input pattern in the
 * design system, and nothing else in the app needs one. It is built only from
 * the existing Input and Button primitives.
 *
 * The whole list is posted on every save, so this owns the array outright and
 * hands it back on every keystroke — the same contract TargetPicker has with
 * its ids.
 */
export function NotificationRecipients({
  emails,
  max,
  error,
  onChange,
  readOnly = false,
}: {
  emails: string[]
  max: number
  error?: string
  onChange: (emails: string[]) => void
  /** Read only access: the addresses, and no way to add or remove one. */
  readOnly?: boolean
}) {
  // A row that has never held anything still needs a stable key, or React
  // remounts every input below the one that was removed and the focus jumps.
  const keys = React.useRef<number[]>([])
  const nextKey = React.useRef(0)
  while (keys.current.length < emails.length) keys.current.push(nextKey.current++)
  if (keys.current.length > emails.length) keys.current.length = emails.length

  const setAt = (index: number, value: string) =>
    onChange(emails.map((email, i) => (i === index ? value : email)))

  const removeAt = (index: number) => {
    keys.current.splice(index, 1)
    onChange(emails.filter((_, i) => i !== index))
  }

  const add = () => {
    keys.current.push(nextKey.current++)
    onChange([...emails, ""])
  }

  const atMax = emails.length >= max

  return (
    <div className="space-y-3">
      {emails.length === 0 ? (
        <p className="text-sm text-ink-muted">
          No one is emailed when this runs.
        </p>
      ) : (
        <ul className="space-y-2" aria-label="Addresses notified after each run">
          {emails.map((email, index) => (
            <li key={keys.current[index]} className="flex items-center gap-2">
              <Input
                type="email"
                inputMode="email"
                autoComplete="off"
                className="max-w-md"
                value={email}
                placeholder="agent@iupat.org"
                aria-label={`Email address ${index + 1}`}
                aria-invalid={Boolean(error)}
                readOnly={readOnly}
                onChange={(event) => setAt(index, event.target.value)}
              />
              {!readOnly && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={
                  email ? `Remove ${email}` : `Remove address ${index + 1}`
                }
                onClick={() => removeAt(index)}
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-xs text-danger-display">{error}</p>}

      {!readOnly && (
      <div className="flex items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={add} disabled={atMax}>
          <Plus aria-hidden="true" className="h-3.5 w-3.5" />
          Add an address
        </Button>
        {atMax && (
          <p className="text-xs text-ink-muted">
            {max} addresses is the most one setup can notify.
          </p>
        )}
      </div>
      )}
    </div>
  )
}
